import {
  faceDisplayName,
  normalizeFontName,
  normalizeFontWeight,
} from "./fonts.js";

export const SKIP_PERSIST_ITALIC = "italic";

const CANONICAL_FAMILY_NAMES = new Set([
  "times new roman",
  "courier new",
  "comic sans ms",
  "arial black",
  "lucida sans unicode",
  "palatino linotype",
  "segoe ui",
  "microsoft sans serif",
]);

const WEIGHT_SUFFIXES = [
  { key: "extralight", display: "ExtraLight", weight: 200 },
  { key: "ultralight", display: "UltraLight", weight: 200 },
  { key: "extrabold", display: "ExtraBold", weight: 800 },
  { key: "ultrabold", display: "UltraBold", weight: 800 },
  { key: "semibold", display: "SemiBold", weight: 600 },
  { key: "demibold", display: "DemiBold", weight: 600 },
  { key: "thin", display: "Thin", weight: 100 },
  { key: "light", display: "Light", weight: 300 },
  { key: "medium", display: "Medium", weight: 500 },
  { key: "bold", display: "Bold", weight: 700 },
  { key: "heavy", display: "Heavy", weight: 900 },
  { key: "regular", display: "Regular", weight: 400 },
  { key: "normal", display: "Regular", weight: 400 },
];

const SLANT_SUFFIXES = ["italic", "oblique"];

const nameHasSlant = (name = "") =>
  /(?:^|\s)(italic|oblique)$/i.test(normalizeFontName({ name }));

const isPlaceholderSubfamily = (name = "") => {
  const normalized = normalizeFontName({ name }).toLowerCase();
  return !normalized || normalized === "regular";
};

const stripWeightSuffix = (familyName) => {
  const normalized = normalizeFontName({ name: familyName });
  const lower = normalized.toLowerCase();
  const candidates = [];

  WEIGHT_SUFFIXES.forEach((token) => {
    SLANT_SUFFIXES.forEach((slant) => {
      candidates.push({
        suffix: ` ${token.key} ${slant}`,
        display: token.display,
        weight: token.weight,
        italic: true,
      });
    });
    candidates.push({
      suffix: ` ${token.key}`,
      display: token.display,
      weight: token.weight,
      italic: false,
    });
  });
  SLANT_SUFFIXES.forEach((slant) => {
    candidates.push({
      suffix: ` ${slant}`,
      display: null,
      weight: null,
      italic: true,
    });
  });

  return (
    candidates
      .map((candidate) => {
        if (!lower.endsWith(candidate.suffix)) return null;
        const rest = normalized
          .slice(0, normalized.length - candidate.suffix.length)
          .trim();
        if (!rest) return null;
        return {
          familyName: rest,
          strippedToken: candidate.display,
          weight: candidate.weight,
          italic: candidate.italic,
        };
      })
      .find(Boolean) || null
  );
};

export const getItalicSkipPersist = ({
  kind,
  italic = false,
  namedInstances,
} = {}) => {
  if (kind === "variable") {
    const upright = Object.values(namedInstances || {}).filter(
      (instance) => !instance?.italic
    );
    return upright.length ? null : SKIP_PERSIST_ITALIC;
  }
  return italic ? SKIP_PERSIST_ITALIC : null;
};

export const canonicalFontFamilyName = ({ name } = {}) => {
  const normalized = normalizeFontName({ name });
  if (!normalized) return "";
  if (CANONICAL_FAMILY_NAMES.has(normalized.toLowerCase())) return normalized;
  return stripWeightSuffix(normalized)?.familyName || normalized;
};

const identityFromFamilyName = ({
  familyName,
  rawSubfamily,
  os2Weight,
  flaggedItalic,
}) => {
  const canonicalKey = familyName.toLowerCase();
  const stripped = CANONICAL_FAMILY_NAMES.has(canonicalKey)
    ? null
    : stripWeightSuffix(familyName);
  const nextItalic = flaggedItalic || Boolean(stripped?.italic);
  const weight =
    stripped?.weight != null
      ? stripped.weight
      : normalizeFontWeight(os2Weight);

  if (!stripped) {
    return {
      familyName,
      faceName:
        rawSubfamily || faceDisplayName({ weight, italic: nextItalic }),
      weight,
      italic: nextItalic,
    };
  }

  const faceFromStrip =
    stripped.strippedToken ||
    (nextItalic ? "Italic" : faceDisplayName({ weight, italic: nextItalic }));

  return {
    familyName: stripped.familyName,
    faceName: isPlaceholderSubfamily(rawSubfamily)
      ? faceFromStrip
      : rawSubfamily,
    weight,
    italic: nextItalic,
    strippedToken: stripped.strippedToken || undefined,
  };
};

export const resolveFontFamilyIdentity = ({
  preferredFamily,
  fontFamily,
  preferredSubfamily,
  fontSubfamily,
  os2Weight,
  italic = false,
} = {}) => {
  const nextPreferredFamily = normalizeFontName({ name: preferredFamily });
  const nextFontFamily = normalizeFontName({ name: fontFamily });
  const nextPreferredSubfamily = normalizeFontName({
    name: preferredSubfamily,
  });
  const nextFontSubfamily = normalizeFontName({ name: fontSubfamily });
  const rawSubfamily = nextPreferredSubfamily || nextFontSubfamily;
  const flaggedItalic = Boolean(italic) || nameHasSlant(rawSubfamily);

  return identityFromFamilyName({
    familyName: nextPreferredFamily || nextFontFamily,
    rawSubfamily,
    os2Weight,
    flaggedItalic,
  });
};
